import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Stop} from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import MicIcon from "@mui/icons-material/Mic";
import PostAddIcon from "@mui/icons-material/PostAdd";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  List,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import {Trans, useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import FeatureGuard from "../../hocs/feature-guard";
import {useSpeechRecognition} from "../../hooks/useSpeechRecognition";
import AlertDialog from "../alert-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import AiChatIcon from "../custom-icons/AiChatIcon";
import ChatMessage from "./ChatMessage";
import {SENDER, STATUS} from "./constants";
import {
  changeAiAssistantChatbotUserMessage,
  resetAiAssistantChatbotChat,
  resetAiAssistantState,
  sendAiAssistantChatbotRequest,
  setAiAssistantChatbotDialogVisibility,
  stopAiAssistantChatbotRequest
} from "../../state/ai-assistant/aiAssistantAction";
import {aiAssistantSelector, aiEnabledFeaturesSelector} from "../../state/ai-assistant/aiAssistantSelector";
import {LICENSE_FEATURES_AI} from "../../state/app/contants";
import {catalogSelector} from "../../state/catalog/catalogSelectors";
import {externalServicesSelector} from "../../state/config/configSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {CATALOG_RESOURCE_TYPE_ODATA, getResourceFromCatalogByType} from "../../utils/catalog";
import "./style.css";

const Chatbot = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const chatRef = useRef(null);

  const nodeId = useSelector(nodeIdSelector);
  const catalog = useSelector(catalogSelector);

  const baseUrl = useSelector(externalServicesSelector)?.ai;
  const ODataBaseUrl = useSelector(externalServicesSelector)?.odata;

  const isSearchEnabled = useSelector(aiEnabledFeaturesSelector)?.search || false;
  const isOdataSearchEnabled = useSelector(aiEnabledFeaturesSelector)?.oDataSearch || false;

  const visible = useSelector(aiAssistantSelector)?.visible;
  const sessionId = useSelector(aiAssistantSelector)?.sessionId;
  const message = useSelector(aiAssistantSelector)?.message;
  const history = useSelector(aiAssistantSelector)?.history;
  const isFullscreen = useSelector(aiAssistantSelector)?.isFullscreen;
  const packageId = useSelector(aiAssistantSelector)?.packageId;
  const lastQuery = useSelector(aiAssistantSelector)?.lastQuery;

  const [disclaimerVisibility, setDisclaimerVisibility] = useState(false);

  const isChatbotAvailable = useMemo(() => {
    if (!packageId) {
      return isSearchEnabled;
    } else {
      if (!isOdataSearchEnabled) {
        return false;
      }
      const odataResource = getResourceFromCatalogByType(catalog, packageId, CATALOG_RESOURCE_TYPE_ODATA);
      return odataResource !== null;
    }
  }, [catalog, packageId, isSearchEnabled, isOdataSearchEnabled]);

  const isSubmitDisabled = useMemo(() => {
    const emptyMessage = (message ?? "").trim().length === 0;
    const pendingOrLoadingResponse = history.some(({status}) => status === STATUS.pending || status === STATUS.loading);
    return emptyMessage || pendingOrLoadingResponse;
  }, [message, history]);

  const isPendingResponse = useMemo(() => history.some(({status}) => status === STATUS.pending), [history]);

  useEffect(() => {
    return () => {
      dispatch(resetAiAssistantState());
    };
  }, [dispatch]);

  const setDialogVisibility = () => {
    dispatch(setAiAssistantChatbotDialogVisibility(!visible));
  };

  const resetChat = () => {
    dispatch(resetAiAssistantChatbotChat());
  };

  const scrollToBottom = useCallback(() => {
    const chat = chatRef.current;
    if (chat) {
      const scroll = () => {
        chat.scrollTop = chat.scrollHeight - chat.clientHeight;
      };
      setTimeout(() => {
        requestAnimationFrame(scroll);
      }, 0);
    }
  }, [chatRef]);

  const handleSubmit = e => {
    e.preventDefault();
    if ((message ?? "").trim().length > 0) {
      dispatch(
        sendAiAssistantChatbotRequest(nodeId, sessionId, message, packageId, catalog, ODataBaseUrl, baseUrl, lastQuery)
      );
    }
  };

  const speechRecognition = useSpeechRecognition();
  useEffect(() => {
    if (speechRecognition?.transcript?.length > 0) {
      dispatch(changeAiAssistantChatbotUserMessage(speechRecognition.transcript, true));
    }
  }, [dispatch, speechRecognition.transcript]);

  const isQuickResponsesVisible = history.some(({isQuickResponseMessage}) => isQuickResponseMessage);

  return (
    isChatbotAvailable && (
      <FeatureGuard featureKey={LICENSE_FEATURES_AI}>
        <>
          <Fab
            color="primary"
            className="chatbot__chat-button"
            onClick={setDialogVisibility}
            aria-label={t("modules.aiAssistant.chatbot.ariaLabel")}
          >
            <AiChatIcon style={{width: "40px", height: "40px"}} />
          </Fab>

          <AlertDialog open={disclaimerVisibility} onClose={() => setDisclaimerVisibility(false)}>
            <CustomDialogTitle onClose={() => setDisclaimerVisibility(false)}>
              {t("modules.aiAssistant.chatbot.disclaimerDialog.title")}
            </CustomDialogTitle>
            <DialogContent>
              <Trans i18nKey="modules.aiAssistant.chatbot.disclaimerDialog.content" />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDisclaimerVisibility(false)}>{t("commons.confirm.close")}</Button>
            </DialogActions>
          </AlertDialog>

          <Dialog
            open={visible}
            onClose={setDialogVisibility}
            fullWidth
            keepMounted
            maxWidth={"sm"}
            aria-labelledby="chatbot__chat-dialog__title"
            slotProps={{
              paper: {
                className: "chatbot__chat-dialog__container",
                sx: [
                  {
                    position: "fixed",
                    right: "16px",
                    margin: "0",
                    transition: "all .8s ease-in-out",
                    display: visible ? "flex" : "none"
                  },
                  isFullscreen
                    ? {
                        bottom: "16px",
                        height: "calc(100% - 32px)",
                        maxHeight: "unset",
                        width: "calc(100% - 32px)",
                        maxWidth: "1200px"
                      }
                    : {
                        bottom: "120px",
                        minHeight: "300px",
                        maxHeight: "calc(100vh - 200px)"
                      }
                ]
              }
            }}
          >
            <CustomDialogTitle
              onClose={setDialogVisibility}
              rightActions={
                <Tooltip title={t("modules.aiAssistant.chatbot.chatDialog.newChat.title")}>
                  <IconButton
                    onClick={() => {
                      resetChat();
                      dispatch(changeAiAssistantChatbotUserMessage(""));
                    }}
                    variant="outlined"
                    color="primary"
                    className="chatbot__chat-dialog__title__new-chat-btn"
                    sx={{padding: "4px"}}
                  >
                    <PostAddIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <Grid container alignItems="center" columnSpacing={"8px"}>
                <Grid className="chatbot__chat-dialog__title__text">
                  <Typography id="chatbot__chat-dialog__title" variant="h6">
                    {t("modules.aiAssistant.chatbot.chatDialog.title")}
                  </Typography>
                </Grid>
                <Grid className="chatbot__chat-dialog__title__disclaimer-btn">
                  <Tooltip title={t("modules.aiAssistant.chatbot.chatDialog.disclaimer.tooltip")}>
                    <IconButton onClick={() => setDisclaimerVisibility(true)} sx={{padding: "4px"}}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </CustomDialogTitle>
            <DialogContent
              className="chatbot__chat-dialog__content"
              sx={{
                padding: "1rem",
                display: "flex",
                flexDirection: "column",
                fontSize: "13px"
              }}
            >
              <Box
                ref={chatRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  marginBottom: "8px"
                }}
              >
                <List>
                  <ChatMessage
                    key={"initial-message"}
                    message={t("modules.aiAssistant.chatbot.chatDialog.messages.initial")}
                    sender={SENDER.bot}
                    status={STATUS.success}
                  />
                  {history.map((entry, index) => (
                    <ChatMessage
                      key={index}
                      messageIdx={index}
                      message={message}
                      {...entry}
                      scrollToBottom={scrollToBottom}
                    />
                  ))}
                </List>
              </Box>
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  autoComplete="off"
                  variant="outlined"
                  label={t("modules.aiAssistant.chatbot.chatDialog.inputText.label")}
                  value={message}
                  onChange={e => dispatch(changeAiAssistantChatbotUserMessage(e.target.value))}
                  disabled={isQuickResponsesVisible}
                  slotProps={{
                    input: {
                      className: "chatbot__chat-dialog__content__input-text",
                      endAdornment: (
                        <>
                          {(message ?? "").trim().length > 0 && (
                            <InputAdornment
                              className="chatbot__chat-dialog__content__input-text__reset-btn"
                              sx={{
                                marginRight: "2px"
                              }}
                            >
                              <IconButton
                                onClick={() => dispatch(changeAiAssistantChatbotUserMessage(""))}
                                disabled={isQuickResponsesVisible}
                                aria-label={t(
                                  "modules.aiAssistant.chatbot.chatDialog.inputText.actions.clear.ariaLabel"
                                )}
                              >
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          )}
                          {speechRecognition.isSupported && (
                            <InputAdornment className="chatbot__chat-dialog__content__input-text__speech-btn">
                              <IconButton
                                onClick={() =>
                                  speechRecognition.isListening ? speechRecognition.stop() : speechRecognition.start()
                                }
                                disabled={isQuickResponsesVisible}
                                aria-label={t(
                                  "modules.aiAssistant.chatbot.chatDialog.inputText.actions.speechRecognition.ariaLabel"
                                )}
                              >
                                <MicIcon
                                  className={`chatbot__chat-dialog__speech-icon ${speechRecognition.isListening ? "chatbot__chat-dialog__speech-icon--listening" : ""}`}
                                />
                              </IconButton>
                            </InputAdornment>
                          )}
                          {isPendingResponse ? (
                            <InputAdornment
                              className="chatbot__chat-dialog__content__input-text__stop-btn"
                              sx={{
                                marginRight: "-8px"
                              }}
                            >
                              <IconButton
                                aria-label={t(
                                  "modules.aiAssistant.chatbot.chatDialog.inputText.actions.stop.ariaLabel"
                                )}
                                onClick={() => dispatch(stopAiAssistantChatbotRequest())}
                                color="primary"
                              >
                                <Stop />
                              </IconButton>
                            </InputAdornment>
                          ) : (
                            <InputAdornment
                              className="chatbot__chat-dialog__content__input-text__send-btn"
                              sx={{
                                marginRight: "-8px"
                              }}
                            >
                              <IconButton
                                aria-label={t(
                                  "modules.aiAssistant.chatbot.chatDialog.inputText.actions.send.ariaLabel"
                                )}
                                type="submit"
                                color="primary"
                                disabled={isSubmitDisabled || isQuickResponsesVisible}
                              >
                                <SendIcon />
                              </IconButton>
                            </InputAdornment>
                          )}
                        </>
                      )
                    }
                  }}
                />
              </form>
            </DialogContent>
          </Dialog>
        </>
      </FeatureGuard>
    )
  );
};

export default Chatbot;
